import React from 'react';
import { AlertTriangle, Settings, Phone, Brain, Mic2, Volume2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CallErrorDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  errorMessage: string;
  onRetry?: () => void;
  onGoToProviders?: () => void;
}

export function CallErrorDialog({
  isOpen,
  onOpenChange,
  errorMessage,
  onRetry,
  onGoToProviders,
}: CallErrorDialogProps) {
  // Parse the error message to identify missing requirements
  const isPrerequisiteError = errorMessage.includes("Missing requirements:");
  const missingRequirements = isPrerequisiteError
    ? errorMessage.split("Missing requirements:")[1]?.split("Please configure")[0]?.trim()
    : null;

  // Helper function to get icon for requirement type
  const getRequirementIcon = (requirement: string) => {
    if (requirement.toLowerCase().includes('llm') || requirement.toLowerCase().includes('openai') || requirement.toLowerCase().includes('anthropic')) {
      return <Brain className="h-4 w-4 text-blue-500" />;
    }
    if (
      requirement.toLowerCase().includes('stt') ||
      requirement.toLowerCase().includes('deepgram') ||
      requirement.toLowerCase().includes('cartesia')
    ) {
      return <Mic2 className="h-4 w-4 text-green-500" />;
    }
    if (requirement.toLowerCase().includes('tts') || requirement.toLowerCase().includes('elevenlabs')) {
      return <Volume2 className="h-4 w-4 text-purple-500" />;
    }
    if (requirement.toLowerCase().includes('phone') || requirement.toLowerCase().includes('plivo')) {
      return <Phone className="h-4 w-4 text-orange-500" />;
    }
    return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Call Failed
          </DialogTitle>
          <DialogDescription>
            {isPrerequisiteError 
              ? "Your organization is missing required provider credentials to make calls."
              : "There was an issue initiating the call."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isPrerequisiteError && missingRequirements ? (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Missing Requirements:</strong>
                <div className="mt-2 space-y-1">
                  {missingRequirements.split('\n').filter(line => line.trim()).map((requirement, index) => {
                    const cleanReq = requirement.replace(/^\d+\.\s*/, '').trim();
                    if (!cleanReq) return null;
                    
                    return (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        {getRequirementIcon(cleanReq)}
                        <span>{cleanReq}</span>
                      </div>
                    );
                  })}
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {errorMessage}
              </AlertDescription>
            </Alert>
          )}

          {isPrerequisiteError && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">What do I need to do?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Go to the <strong>Providers</strong> tab in your organization settings</li>
                <li>• Configure the missing provider credentials</li>
                <li>• Ensure you have at least one Plivo phone number configured</li>
                <li>• Try making the call again</li>
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <div className="flex gap-2">
            {isPrerequisiteError && onGoToProviders && (
              <Button onClick={onGoToProviders} className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Go to Providers
              </Button>
            )}
            {onRetry && !isPrerequisiteError && (
              <Button onClick={onRetry}>
                Retry Call
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
